import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import CardsTable from "@/components/CardsTable";
import VLFModal from "@/components/Modal";
import HandleCareerModal from "@/pages/Scouting/Players/Player/handleCareer";

import {StaffAPI} from "@/api/staff";

import ArrowBack from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ControlPointIcon from "@mui/icons-material/ControlPoint";

import {
	dateFormat,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {useTranslation} from "react-i18next";

const ScoutingStaffPage = (props) => {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [staff, setStaff] = useState({});

	const [initialData, setInitialData] = useState();

	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openCareerDetail, setOpenCareerDetail] = useState(false);

	const getStaff = async () => {
		setLoading(true);
		try {
			const response = await StaffAPI.getById(id);
			response.career = response.career?.map((c) => {
				if (c.id?.startsWith("00000000")) {
					c.readable = true;
				}
				return c;
			});

			// controllo eventuali righe doppie per stagione-squadra-categoria
			const seen = new Set();
			response.career = response.career.filter((obj) => {
				const key = `${obj.season?.name}-${obj.club?.name}-${obj.playerCategory?.name}`;
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			});

			setStaff(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteStaff = async () => {
		setLoading(true);
		try {
			await StaffAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/scouting/staffs");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	useEffect(() => {
		getStaff();
	}, []);

	const handleCareerDetail = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				...data,
				staffId: staff?.id,
				seasonId: data?.careerSeason?.value,
				clubId: data?.careerClub?.value,
				playerCategoryId: data?.careerCategory?.value,
			};

			id
				? await StaffAPI.updateCareer(staff?.id, id, body)
				: await StaffAPI.createCareer(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getStaff();
			setOpenCareerDetail(false);
			setInitialData();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteCareerDetail = async (id) => {
		setLoading(true);
		try {
			await StaffAPI.deleteCareer(staff?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getStaff();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const goBack = () => {
		navigate("/scouting/staffs");
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate("/scouting/staffs/" + staff?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const mainConfiguration = [
		{
			value: staff?.firstName,
			label: t("name"),
			size: "col-6 col-lg-4",
		},
		{
			value: staff?.lastName,
			label: "Cognome",
			size: "col-6 col-lg-4",
		},
		{
			value: staff?.dateOfBirth
				? moment(staff?.dateOfBirth).format(dateFormat)
				: null,
			label: "Data di nascita",
			size: "col-6 col-lg-4",
		},
		{value: staff?.unit?.name, label: "Area", size: "col-6 col-lg-4"},
		{value: staff?.role?.name, label: "Ruolo", size: "col-6 col-lg-4"},
	];

	const careerColumns = [
		{
			label: "Stagione",
			key: "season",
			customRender: (item) => item.season?.name,
		},
		{
			label: "Squadra",
			key: "club",
			customRender: (item) => item.club?.name,
		},
		{
			label: "Categoria",
			key: "playerCategory",
			customRender: (item) => item.playerCategory?.name,
		},
	];

	const breadcrumbsElements = [
		{label: "Allenatori", path: "/scouting/staffs"},
		{label: "/"},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					staff ? (staff.firstName || "") + " " + (staff.lastName || "") : ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12 col-md-2">
										<div className="image-container generic">
											<img
												src={require("@/assets/images/manager/staff.png")}
												alt=""
											/>
										</div>
									</div>
									<div className="col-sm-12 col-md-10">
										<ViewField configuration={mainConfiguration} />
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
				<div className="row no-margin mt-3">
					<div className="col-sm-12 padding-small">
						<CardsTable
							title="Carriera"
							fullHeight={false}
							middleHeight
							configurationColumns={careerColumns}
							data={staff?.career}
							actions={[
								{
									icon: <ControlPointIcon />,
									onClick: () => setOpenCareerDetail(true),
								},
							]}
							handleEdit={(item) => {
								setInitialData(Object.assign({}, item));
								setOpenCareerDetail(true);
							}}
							handleDelete={(item) => {
								deleteCareerDetail(item.id);
							}}
							defaultSortedField="dateEvent"
							defaultSortedOrder="descend"
						/>
					</div>
				</div>
				<VLFModal
					open={openHandleConfirm}
					title="Eliminazione"
					content={
						<div className="mt-2">
							Sei sicuro di voler eliminare questo elemento?
						</div>
					}
					confirm={() => {
						deleteStaff();
					}}
					close={() => setOpenHandleConfirm(false)}
					confirmLabel="Conferma"
				/>
				{openCareerDetail ? (
					<HandleCareerModal
						open={openCareerDetail}
						confirm={handleCareerDetail}
						close={() => {
							setOpenCareerDetail(false);
							setInitialData();
						}}
						initialData={initialData}
						handleNotification={handleNotification}
					/>
				) : null}
			</div>
		</Page>
	);
};

export default ScoutingStaffPage;
